import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { usePageMeta } from '@/hooks/usePageMeta';
import SmartBackLink from '@/components/common/SmartBackLink';


export default function TermsAndConditions() {
  usePageMeta({
    title: 'Terms and Conditions',
    description: 'LANERUNNER terms and conditions of use. Read our platform rules, user agreements, and legal terms for freight brokers and carriers.'
  });
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4 py-4">
          <SmartBackLink />
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-3">Terms and Conditions</h1>
            <p className="text-muted-foreground">Last Updated: June 25, 2026</p>
          </div>

          {/* Content Card */}
          <Card>
            <CardContent className="p-8 md:p-12">
              <div className="prose prose-neutral dark:prose-invert max-w-none">
                
                {/* Agreement to Terms */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">AGREEMENT TO TERMS</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    These Terms and Conditions constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you") and LaneRunner, Inc. ("Company," "we," "us," or "our"), concerning your access to and use of the lanerunner.com website as well as any other media form, media channel, mobile website or mobile application related, linked, or otherwise connected thereto (collectively, the "Site"). You agree that by accessing the Site, you have read, understood, and agreed to be bound by all of these Terms and Conditions. <strong>IF YOU DO NOT AGREE WITH ALL OF THESE TERMS AND CONDITIONS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE SITE AND YOU MUST DISCONTINUE USE IMMEDIATELY.</strong>
                  </p>
                </section>

                {/* 1. Services */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">1. SERVICES</h2>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    LaneRunner provides a subscription-based freight load board platform (SaaS) connecting carriers with shippers and freight brokers. Our services include but are not limited to:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-1">
                    <li>Load posting and searching</li>
                    <li>Carrier and shipper profiles</li>
                    <li>Communication tools between parties</li>
                    <li>Rate information and market data</li>
                    <li>SMS notifications and alerts</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    LaneRunner operates as a SaaS platform only. We do not process payments between carriers and shippers/brokers, nor do we charge transaction fees. All payments for freight services are handled directly between the parties.
                  </p>
                </section>

                {/* 2. User Registration and Accounts */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">2. USER REGISTRATION AND ACCOUNTS</h2>

                  <h3 className="text-xl font-semibold mb-3">2.1 Account Creation</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You must register for an account to access certain features of the Site. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">2.2 Account Security</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to immediately notify us of any unauthorized use of your account or any other breach of security.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">2.3 Account Types</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Different account types (carrier, shipper, broker) have different features and requirements. You agree to only register for account types appropriate to your business and to maintain any required licenses, insurance, or certifications.
                  </p>
                </section>

                {/* 3. SMS Messaging Terms */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">3. SMS MESSAGING TERMS</h2>

                  <h3 className="text-xl font-semibold mb-3">3.1 Consent to Receive Messages</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3 font-semibold">
                    SMS notifications are optional. You can create and use a LaneRunner account without ever consenting to receive SMS messages. SMS consent is never a condition of signing up for or using LaneRunner.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    By providing your mobile phone number and separately opting in to SMS notifications (via a dedicated checkbox at signup or in account settings), you consent to receive text messages from LaneRunner for both transactional and promotional purposes as described in your opt-in preferences. You may decline SMS consent and still complete signup.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Users may opt in to SMS notifications through any of the following methods:
                  </p>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li>(a) Visiting lanerunner.com/sms-signup, providing a mobile phone number, checking the consent checkbox, and completing phone verification.</li>
                    <li>(b) Enabling SMS in account notification settings after creating an account at lanerunner.com, then completing phone verification.</li>
                    <li>(c) Texting START to our SMS number to re-enable notifications after previously opting out.</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    In all cases, consent is logged with timestamp and the exact consent language presented.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">3.2 Transactional Messages</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You will receive transactional messages including two-factor authentication codes, security alerts, account notifications, and booking confirmations. These messages are required for account security and platform functionality and cannot be disabled.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">3.3 Promotional Messages</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    If you opt in to promotional messages, you may receive load alerts, rate updates, and market information. You can opt out of promotional messages at any time by texting STOP to our messaging number or by updating your preferences in your account settings.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">3.4 Message Frequency and Charges</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Message frequency varies based on your account activity and alert preferences. You may receive up to 15-20 messages per day. Standard message and data rates from your mobile carrier may apply. We do not charge for SMS messages.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">3.5 Supported Carriers</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Our SMS service is available on major carriers including AT&T, Verizon, T-Mobile, Sprint, US Cellular, Boost, MetroPCS, and others. Service availability may vary.
                  </p>
                </section>

                {/* 4. User Responsibilities */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">4. USER RESPONSIBILITIES</h2>

                  <h3 className="text-xl font-semibold mb-3">4.1 Accurate Information</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You agree to provide accurate information about loads, rates, equipment, and availability. You agree not to post false, misleading, or fraudulent load information.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">4.2 Compliance with Laws</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You agree to comply with all applicable federal, state, and local laws including but not limited to FMCSA regulations, DOT requirements, and all transportation laws.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">4.3 Insurance and Licensing</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Carriers must maintain appropriate commercial auto liability insurance, cargo insurance, and all required operating authorities and licenses. You agree to provide proof of insurance and licensing upon request.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">4.4 Prohibited Conduct</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">You agree not to:</p>
                  <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-1">
                    <li>Use the Site for any illegal purpose</li>
                    <li>Impersonate any person or entity</li>
                    <li>Post spam or unsolicited commercial communications</li>
                    <li>Attempt to gain unauthorized access to the Site or other users' accounts</li>
                    <li>Use automated systems (bots) without our express written permission</li>
                    <li>Harass, abuse, or harm other users</li>
                    <li>Post loads you do not have authority to post</li>
                    <li>Engage in double brokering or other fraudulent practices</li>
                  </ul>
                </section>

                {/* 5. Load Postings and Transactions */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">5. LOAD POSTINGS AND TRANSACTIONS</h2>

                  <h3 className="text-xl font-semibold mb-3">5.1 Load Postings</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Shippers and brokers are responsible for the accuracy of load postings. LaneRunner does not verify load details and is not responsible for inaccurate or fraudulent postings.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">5.2 Independent Contractors</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    LaneRunner facilitates connections between parties but is not a party to any transportation agreement. All agreements are between the carrier and the shipper/broker directly. Carriers are independent contractors and not employees of LaneRunner.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">5.3 Payment Terms</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Payment terms are negotiated and handled directly between carriers and shippers/brokers. LaneRunner does not process payments for freight services and is not responsible for payment disputes or non-payment.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">5.4 Disputes</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    LaneRunner is not responsible for disputes between carriers and shippers/brokers regarding payment, load details, delivery, damage, or any other matters. Such disputes must be resolved directly between the parties.
                  </p>
                </section>

                {/* 6. Subscription Fees and Payment */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">6. SUBSCRIPTION FEES AND PAYMENT</h2>

                  <h3 className="text-xl font-semibold mb-3">6.1 Subscription Model</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    LaneRunner operates on a subscription-based SaaS model. Access to the platform requires payment of subscription fees based on your selected plan.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">6.2 Subscription Fees</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You agree to pay all subscription fees associated with your account type. Subscription fees are billed monthly or annually as selected. Fees are subject to change with 30 days' notice.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">6.3 No Transaction Fees</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    LaneRunner does not charge transaction fees or take a percentage of any load payments. All freight payments are handled directly between carriers and shippers/brokers.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">6.4 Payment Methods</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You agree to provide current, complete, and accurate payment information for your subscription. You authorize us to charge your designated payment method for all subscription fees.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">6.5 Late Payment and Non-Payment</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Failure to pay subscription fees may result in suspension or termination of your account and access to the Site. Subscriptions not paid by the due date may incur late fees or service interruption.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">6.6 Refunds</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Subscription fees are non-refundable except as required by law or as otherwise specified in your subscription agreement.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">6.7 Cancellation</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    You may cancel your subscription at any time. Cancellation will be effective at the end of your current billing period. No refunds will be provided for partial subscription periods.
                  </p>
                </section>

                {/* 7. Intellectual Property Rights */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">7. INTELLECTUAL PROPERTY RIGHTS</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    The Site and its entire contents, features, and functionality are owned by LaneRunner, Inc. and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Site without our prior written consent.
                  </p>
                </section>

                {/* 8. Limitation of Liability */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">8. LIMITATION OF LIABILITY</h2>

                  <h3 className="text-xl font-semibold mb-3">8.1 Disclaimer of Warranties</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    THE SITE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. WE MAKE NO WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SITE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">8.2 No Guarantee of Results</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    WE DO NOT GUARANTEE THAT YOU WILL FIND LOADS, SECURE FREIGHT, OR GENERATE ANY SPECIFIC REVENUE FROM USE OF THE PLATFORM. SUCCESS DEPENDS ON MANY FACTORS OUTSIDE OUR CONTROL.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">8.3 Limitation of Liability</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    TO THE FULLEST EXTENT PERMITTED BY LAW, LANERUNNER SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
                  </p>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li>Your use or inability to use the Site</li>
                    <li>Any unauthorized access to or use of our servers</li>
                    <li>Any interruption or cessation of transmission to or from the Site</li>
                    <li>Any bugs, viruses, or other harmful code</li>
                    <li>Any errors or omissions in content</li>
                    <li>Any defamatory, offensive, or illegal conduct of any third party</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">8.4 Maximum Liability</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM OR RELATED TO THE SITE SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN SUBSCRIPTION FEES IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR $100, WHICHEVER IS GREATER.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">8.5 Third-Party Conduct</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We are not liable for the conduct of any user, carrier, shipper, or broker. We do not guarantee the safety, quality, or legality of loads posted, the truth or accuracy of user information, or the ability of users to complete transactions.
                  </p>
                </section>

                {/* 9. Indemnification */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">9. INDEMNIFICATION</h2>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    You agree to indemnify, defend, and hold harmless LaneRunner, Inc., its officers, directors, employees, contractors, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees and costs, arising out of or in any way connected with:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-1">
                    <li>Your access to or use of the Site</li>
                    <li>Your violation of these Terms</li>
                    <li>Your violation of any rights of another party</li>
                    <li>Your violation of any applicable laws or regulations</li>
                    <li>Any freight transactions or disputes between you and other users</li>
                  </ul>
                </section>

                {/* 10. Termination */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">10. TERMINATION</h2>

                  <h3 className="text-xl font-semibold mb-3">10.1 Termination by You</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You may terminate your account and cancel your subscription at any time by contacting customer support at support@lanerunner.com or using the account cancellation feature in your settings. Cancellation is effective at the end of your current billing period.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">10.2 Termination by Us</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    We may terminate or suspend your account immediately, without prior notice or liability, for any reason, including but not limited to:
                  </p>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li>Breach of these Terms</li>
                    <li>Fraudulent activity</li>
                    <li>Non-payment of subscription fees</li>
                    <li>Violation of applicable laws</li>
                    <li>Harmful conduct toward other users</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">10.3 Effect of Termination</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Upon termination, your right to use the Site will immediately cease. Any outstanding subscription payment obligations will remain in effect. We may delete your account data in accordance with our data retention policies, though we may retain certain information as required by law or for legitimate business purposes.
                  </p>
                </section>

                {/* 11. Dispute Resolution */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">11. DISPUTE RESOLUTION</h2>

                  <h3 className="text-xl font-semibold mb-3">11.1 Informal Resolution</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    If you have any dispute with us, you agree to first contact us at support@lanerunner.com and attempt to resolve the dispute informally for at least 30 days before initiating any formal proceedings.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">11.2 Binding Arbitration</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Any dispute arising from or relating to these Terms or the Site that cannot be resolved informally shall be resolved by binding arbitration in accordance with the American Arbitration Association's Commercial Arbitration Rules. The arbitration shall be conducted in Puerto Rico, and judgment on the arbitration award may be entered in any court having jurisdiction.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">11.3 Class Action Waiver</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    YOU AGREE THAT ANY ARBITRATION OR PROCEEDING SHALL BE LIMITED TO THE DISPUTE BETWEEN YOU AND US INDIVIDUALLY. YOU WAIVE ANY RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">11.4 Exceptions</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Notwithstanding the above, either party may seek injunctive relief in court for disputes related to intellectual property rights or unauthorized access to the Site.
                  </p>
                </section>

                {/* 12. Governing Law */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">12. GOVERNING LAW</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    These Terms shall be governed by and construed in accordance with the laws of Puerto Rico and the United States, without regard to conflict of law principles. Any legal action or proceeding arising under these Terms will be brought exclusively in the courts located in Puerto Rico.
                  </p>
                </section>

                {/* 13. Modifications to Terms */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">13. MODIFICATIONS TO TERMS</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on the Site and updating the "Last Updated" date. Your continued use of the Site after such changes constitutes your acceptance of the new Terms.
                  </p>
                </section>

                {/* 14. Severability */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">14. SEVERABILITY</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    If any provision of these Terms is found to be unlawful, void, or unenforceable, that provision shall be deemed severable and shall not affect the validity and enforceability of the remaining provisions.
                  </p>
                </section>

                {/* 15. Entire Agreement */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">15. ENTIRE AGREEMENT</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    These Terms constitute the entire agreement between you and LaneRunner regarding the use of the Site and supersede all prior agreements and understandings, whether written or oral.
                  </p>
                </section>

                {/* 16. Contact Information */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">16. CONTACT INFORMATION</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    If you have any questions about these Terms, please contact us:
                  </p>
                  <div className="bg-muted/50 rounded-lg p-6 mb-4">
                    <p className="font-semibold mb-2">LaneRunner, Inc.</p>
                    <p className="text-muted-foreground mb-1">Email: support@lanerunner.com</p>
                    <p className="text-muted-foreground">Phone: (855) 918-3520</p>
                  </div>
                </section>

                {/* 17. Additional Provisions */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">17. ADDITIONAL PROVISIONS</h2>

                  <h3 className="text-xl font-semibold mb-3">17.1 No Agency</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Nothing in these Terms creates any agency, partnership, joint venture, or employment relationship between you and LaneRunner.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">17.2 Force Majeure</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    We shall not be liable for any delay or failure to perform resulting from causes outside our reasonable control, including but not limited to acts of God, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, accidents, pandemics, strikes, or shortages of transportation facilities, fuel, energy, labor, or materials.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">17.3 Waiver</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any other term, and our failure to assert any right or provision under these Terms shall not constitute a waiver of such right or provision.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">17.4 Assignment</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You may not assign or transfer these Terms or your account without our prior written consent. We may assign these Terms without restriction.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">17.5 Survival</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Provisions that by their nature should survive termination shall survive, including but not limited to ownership provisions, warranty disclaimers, indemnification, and limitations of liability.
                  </p>
                </section>

                {/* 18. FMCSA Data Usage Disclosure */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">18. FMCSA DATA USAGE DISCLOSURE</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    <em>Last Updated: May 5, 2026</em>
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    LaneRunner Inc. ("LaneRunner") incorporates publicly available data from the U.S. Department of Transportation's Federal Motor Carrier Safety Administration (FMCSA) Safety and Fitness Electronic Records (SAFER) system into our Services. This disclosure explains how we use this data and what users should know.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">18.1 Source of Data</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    LaneRunner queries the FMCSA SAFER public database and other publicly available FMCSA resources to retrieve information about motor carriers, freight brokers, and shippers, including but not limited to:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground leading-relaxed mb-4 space-y-1">
                    <li>USDOT and MC numbers</li>
                    <li>Operating authority status</li>
                    <li>Insurance coverage status</li>
                    <li>Surety bond status (for brokers)</li>
                    <li>Safety ratings</li>
                    <li>Inspection and crash history</li>
                    <li>Equipment counts</li>
                    <li>Years in operation</li>
                    <li>Officer and contact information</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    This data is provided by the FMCSA and is publicly accessible at{' '}
                    <a href="https://safer.fmcsa.dot.gov" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                      https://safer.fmcsa.dot.gov
                    </a>.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">18.2 We Are Not Affiliated with FMCSA</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    LaneRunner is a private, independent company. We are not affiliated with, endorsed by, sponsored by, or in any way officially connected to the FMCSA, the U.S. Department of Transportation, or the U.S. government. We do not represent ourselves as an official FMCSA service, and our use of FMCSA data is permitted under the data's public availability.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">18.3 Data Accuracy and Timing</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    FMCSA data is updated on FMCSA's own schedule, which may not be real-time. While LaneRunner makes commercially reasonable efforts to retrieve current data, we cannot guarantee:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground leading-relaxed mb-4 space-y-1">
                    <li>That data displayed is current as of the moment you view it</li>
                    <li>That all relevant fields are complete or accurate</li>
                    <li>That FMCSA's underlying records are themselves accurate</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Users should treat FMCSA data displayed on LaneRunner as informational only and verify critical information directly through official FMCSA channels before making business decisions.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">18.4 How We Use FMCSA Data</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">LaneRunner uses FMCSA data to:</p>
                  <ul className="list-disc pl-6 text-muted-foreground leading-relaxed mb-4 space-y-1">
                    <li>Verify that signing-up users have valid operating authority</li>
                    <li>Display carrier and broker information on load detail pages</li>
                    <li>Surface compliance signals (insurance status, bond status, authority status)</li>
                    <li>Detect changes in carrier or broker status over time</li>
                    <li>Help users make informed business decisions about counterparties</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    We may cache, aggregate, and store FMCSA data within our systems to improve performance and provide historical context. Cached data is refreshed periodically.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">18.5 Data Disputes</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    If you believe information about your company displayed on LaneRunner is inaccurate, the underlying source data is maintained by FMCSA. Disputes about FMCSA records must be resolved directly with FMCSA at{' '}
                    <a href="https://www.fmcsa.dot.gov" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                      https://www.fmcsa.dot.gov
                    </a>.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    LaneRunner will refresh our cached data upon request. To request a refresh, email{' '}
                    <a href="mailto:support@lanerunner.com" className="text-primary underline">support@lanerunner.com</a>{' '}
                    with your USDOT or MC number.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">18.6 No Consumer Reports</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    LaneRunner is not a consumer reporting agency under the Fair Credit Reporting Act (FCRA), and the data we display is not a "consumer report" within the meaning of that statute. Information displayed on LaneRunner should not be used for credit, employment, insurance underwriting, or other FCRA-regulated purposes.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">18.7 Limitations of Liability</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">LaneRunner is not responsible for:</p>
                  <ul className="list-disc pl-6 text-muted-foreground leading-relaxed mb-4 space-y-1">
                    <li>The accuracy of FMCSA-sourced data</li>
                    <li>Decisions you make based on FMCSA data displayed in our Services</li>
                    <li>Disputes between users arising from FMCSA data</li>
                    <li>Changes to FMCSA's data structure, availability, or terms</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Your use of FMCSA data through LaneRunner is governed by our Terms of Service.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Contact us:{' '}
                    <a href="mailto:support@lanerunner.com" className="text-primary underline">support@lanerunner.com</a>
                  </p>
                </section>

              </div>
            </CardContent>
          </Card>

          {/* Footer */}

          <footer className="mt-12 pt-8 border-t text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-muted-foreground">
              <p>© 2025 LaneRunner, Inc. All rights reserved.</p>
              <div className="flex gap-4">
                <Link to="/privacy" className="hover:text-foreground transition-colors">
                  Privacy Policy
                </Link>
                <span>•</span>
                <a href="mailto:support@lanerunner.com" className="hover:text-foreground transition-colors">
                  Contact
                </a>
              </div>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
}
